import { useCallback, useEffect, useRef, useState } from 'react';

// Modo claro por defecto; el oscuro («Grafito», DESIGN.md §10) se recuerda por navegador.
// public/index.html aplica la clase antes de pintar con la misma clave, para que no destelle.
export const TEMA_KEY = 'alertai_tema';
export type Tema = 'claro' | 'oscuro';

/** Lo que dura la clase `tema-transicion` en <html>: el fundido de index.css (200 ms) más margen. */
const DURACION_FUNDIDO_MS = 250;

const leer = (): Tema => { try { return localStorage.getItem(TEMA_KEY) === 'oscuro' ? 'oscuro' : 'claro'; } catch { return 'claro'; } };

export function useTema(): [Tema, () => void] {
  const [tema, setTema] = useState<Tema>(leer);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', tema === 'oscuro');
    try { localStorage.setItem(TEMA_KEY, tema); } catch { /* no se recuerda */ }
  }, [tema]);

  // Otra pestaña cambió el modo: esta lo sigue.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => { if (e.key === TEMA_KEY) setTema(leer()); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Solo el botón pide fundido (informe apple-design, propuesta 5): la carga inicial y otra pestaña cambian sin él.
  const fundido = useRef<number | undefined>(undefined);
  const alternar = useCallback(() => {
    const html = document.documentElement;
    html.classList.add('tema-transicion');
    // Un clic nuevo reinicia la cuenta: el temporizador anterior no corta el fundido en curso.
    window.clearTimeout(fundido.current);
    fundido.current = window.setTimeout(() => html.classList.remove('tema-transicion'), DURACION_FUNDIDO_MS);
    setTema(prev => (prev === 'oscuro' ? 'claro' : 'oscuro'));
  }, []);
  return [tema, alternar];
}
